// ViewViolationDeviceModal.jsx

import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import API_BASE_URL from "../../../../Constants";
import DeviceAccordion from "../../../setup/components/Device List/components/DeviceAccordion";
import RenderFields from "../InspectionList/RenderFields";
import { topSectionFields, bottomSectionFields, splitFields } from "./ViolationFields";

const ViewViolationDeviceModal = ({
  open,
  onClose,
  refreshList,
  setNext,
  setPrev,
  violationId,
  prefilledData
}) => {
  // If we have partial data from the table row, initialize with that:
  const [violationData, setViolationData] = useState(prefilledData || null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Split into left and right columns
  const [topLeft, topRight] = splitFields(topSectionFields);
  const [bottomLeft, bottomRight] = splitFields(bottomSectionFields);

  // 1) Fetch complete record once the modal opens
  useEffect(() => {
    if (!open || !violationId) return;

    setLoading(true);
    fetch(`${API_BASE_URL}/api/violations/${violationId}`, { credentials: "include" })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`Could not fetch violation #${violationId}. Status: ${resp.status}`);
        }
        return resp.json();
      })
      .then((apiData) => {
        // Merge server data with any prefilled row data
        setViolationData((prev) => ({ ...(prev || {}), ...apiData }));
      })
      .catch((err) => {
        console.error("Error fetching violation:", err);
        setViolationData(null);
      })
      .finally(() => setLoading(false));
  }, [open, violationId]);

  // 2) Close the modal, but only if not in edit mode
  const handleClose = () => {
    if (!isEditMode) {
      onClose();
    }
  };

  // Expand/contract
  const toggleExpand = () => setIsExpanded(!isExpanded);

  // 3) Validation & Save
  const validateFields = () => {
    const errors = {};
    // e.g. if you require violationNumber:
    // if (!violationData?.violationNumber) errors.violationNumber = "Required!";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveChanges = async () => {
    if (!validateFields()) {
      alert("Please fix errors before saving.");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/violations/${violationId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(violationData),
      });
      if (!response.ok) {
        throw new Error(`Save failed. Status: ${response.status}`);
      }
      alert("Saved successfully.");
      setIsEditMode(false);
      if (refreshList) refreshList();
    } catch (error) {
      console.error("Error saving violation:", error);
      alert("Failed to save changes.");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this violation?")) {
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/violations/${violationId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Delete failed. Status: ${response.status}`);
      }
      alert("Violation deleted.");
      onClose();
      if (refreshList) refreshList();
    } catch (err) {
      console.error("Error deleting violation:", err);
      alert("Failed to delete violation.");
    }
  };

  // 4) Field changes
  const handleFieldChange = (e) => {
    const { name, type, checked, value } = e.target;
    setViolationData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  if (!open) return null;

  // Show either 'violationNumber' from the data or fallback to ID
  const displayedNumber = violationData?.violationNumber || violationId || "N/A";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: isExpanded ? "90%" : "1000px",
          maxHeight: "72vh",
          minHeight: "72vh",
        },
      }}
      style={{ zIndex: 1400 }}
    >
      {/* Modal Header */}
      <div className="sticky top-0 bg-white z-10">
        {/* Title row */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Close"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold">
              {isEditMode
                ? `Edit Violation # ${displayedNumber}`
                : `View Violation # ${displayedNumber}`}
            </h2>
          </div>

          {/* Next/Prev, Expand (only if not editing) */}
          <div className="flex items-center space-x-3">
            {!isEditMode && (
              <>
                <button
                  onClick={setPrev}
                  className="p-2 rounded-full hover:bg-gray-200 transition"
                  aria-label="Previous"
                >
                  <ArrowBackIcon />
                </button>
                <button
                  onClick={setNext}
                  className="p-2 rounded-full hover:bg-gray-200 transition"
                  aria-label="Next"
                >
                  <ArrowForwardIcon />
                </button>
              </>
            )}
            <button
              onClick={toggleExpand}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Expand"
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex space-x-4 p-4 bg-gray-50">
          {isEditMode ? (
            <>
              <button
                onClick={() => setIsEditMode(false)}
                className="px-4 py-2 bg-gray-300 text-black font-bold rounded-md hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Save
              </button>
              <button
                onClick={handleDelete}
                className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
              >
                Delete
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditMode(true)}
              className="px-6 py-2 bg-yellow-500 text-white font-bold rounded-md hover:bg-yellow-600 transition"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Modal Body */}
      <DialogContent dividers>
        {loading ? (
          <p className="text-center">Loading violation data...</p>
        ) : !violationData ? (
          <p className="text-center">No violation data found.</p>
        ) : (
          <>
            {/* Main Info */}
            <DeviceAccordion label="Main Violation Information">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-1/2 space-y-4">
                  <RenderFields
                    fields={topLeft}
                    formData={violationData}
                    handleChange={isEditMode ? handleFieldChange : null}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
                <div className="w-full md:w-1/2 space-y-4">
                  <RenderFields
                    fields={topRight}
                    formData={violationData}
                    handleChange={isEditMode ? handleFieldChange : null}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </div>
            </DeviceAccordion>

            {/* Status & Details */}
            <DeviceAccordion label="Violation Status & Details" defaultExpanded>
              <div className="flex flex-col md:flex-row gap-6 bg-yellow-50 p-4 rounded-md">
                <div className="w-full md:w-1/2 space-y-4">
                  <RenderFields
                    fields={bottomLeft}
                    formData={violationData}
                    handleChange={isEditMode ? handleFieldChange : null}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
                <div className="w-full md:w-1/2 space-y-4">
                  <RenderFields
                    fields={bottomRight}
                    formData={violationData}
                    handleChange={isEditMode ? handleFieldChange : null}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </div>
            </DeviceAccordion>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewViolationDeviceModal;
